/**
 * Client-side geometry helpers — spec §G7 ("Geometry helpers (client)").
 *
 * §G7 is explicit that these are **display only**: "backend uses ST_PointOnSurface" for
 * placement, and §H2 recomputes AOI area pixel-exact server-side. Three of the four are
 * §F5.10's measure/AOI maths — `haversineKm`, `polylineKm`, `shoelaceKm2` — and the fourth,
 * `polygonCentroid`, is §F5.5's marker fallback for a unit whose `properties.centroid` the
 * API could not supply.
 */

/** A GeoJSON position, `[lon, lat]`. */
export type Position = [number, number];

/** `[lat, lon]` — Leaflet's order, and the shape §F1 stores in `aoi.pts`. */
export type LatLon = [number, number];

/** Structurally a `L.LatLng`, without importing Leaflet into a pure-maths module. */
export interface LatLngLike {
  lat: number;
  lng: number;
}

export type PointLike = LatLon | LatLngLike;

/** Mean Earth radius, km — the mockup's `R` in `haversine`. */
export const EARTH_RADIUS_KM = 6371;

/**
 * Kilometres per degree at the equator (WGS 84), the constant §G7's area formula uses.
 * Note it is the *ellipsoid* value: `EARTH_RADIUS_KM · π/180` is 111.19, 0.1 % smaller.
 * §G7 names 111.32, so 111.32 it is — the two helpers are consistent to well inside the
 * accuracy either claims.
 */
export const KM_PER_DEGREE = 111.32;

const RAD = Math.PI / 180;

/** Accept either `[lat, lon]` or a Leaflet `LatLng` and normalise to `[lat, lon]`. */
export function toLatLon(p: PointLike): LatLon {
  return Array.isArray(p) ? [p[0], p[1]] : [p.lat, p.lng];
}

/**
 * Great-circle distance in km — §G7 "Haversine km", copied from the mockup's `haversine`.
 *
 * Uses the `asin` form rather than `atan2`: identical for the sub-continental distances
 * the §F5.10 measure tool produces, and it is what the mockup shipped.
 */
export function haversineKm(a: PointLike, b: PointLike): number {
  const [aLat, aLon] = toLatLon(a);
  const [bLat, bLon] = toLatLon(b);
  const dLat = (bLat - aLat) * RAD;
  const dLon = (bLon - aLon) * RAD;
  const q =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(aLat * RAD) * Math.cos(bLat * RAD) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.sqrt(Math.min(1, q)));
}

/**
 * Cumulative length of an open polyline in km — the sum the §F5.10 measure tool prints in
 * its permanent tooltip. Fewer than two points measure nothing, which is 0 km, not NaN.
 */
export function polylineKm(pts: readonly PointLike[]): number {
  let d = 0;
  for (let i = 1; i < pts.length; i++) d += haversineKm(pts[i - 1]!, pts[i]!);
  return d;
}

/**
 * Planar (equirectangular) shoelace area of a closed ring, in km² — §G7's "shoelace
 * spherical-approx area", the number the §F5.10 AOI tooltip shows.
 *
 * **Deviates from the literal §G7 formula, which is wrong by a factor of ~258 at
 * programme latitudes (NOTES #308).** §G7 (and the mockup's `polyAreaKm2` it was copied
 * from) reads `|Σ (x_j+x_i)·sin(mid lat)·(y_j−y_i)| · 111.32² · π/360`, which carries two
 * independent errors:
 *
 * 1. **`sin` where the projection needs `cos`.** Scaling a degree of longitude to km at
 *    latitude φ is `cos φ` — the parallels shrink toward the poles. `sin φ` is 0 at the
 *    equator, i.e. it claims an equatorial polygon has no area at all.
 * 2. **One degrees→radians conversion too many.** `π/360` is `½ · π/180`; the shoelace's
 *    own factor is the ½, and the `π/180` double-converts coordinates that `111.32`
 *    (km per *degree*) has already taken out of degrees.
 *
 * What is implemented is the same shoelace with both fixed —
 * `½ · |Σ (x_j+x_i)·cos(mid lat)·(y_j−y_i)| · 111.32²` — which returns 12,098 km² for a
 * 1°×1° cell at 12–13°N (true value 12,096 km²); §G7's literal text returns 46.8 km².
 * An AOI tooltip is a number a reader acts on, so it has to be the right one.
 *
 * Points are `[lat, lon]` (or `L.LatLng`); the ring is closed implicitly, winding order is
 * irrelevant (the sum is taken absolute), and fewer than three points is 0 km².
 */
export function shoelaceKm2(pts: readonly PointLike[]): number {
  if (pts.length < 3) return 0;
  const ring = pts.map(toLatLon);
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [latI, lonI] = ring[i]!;
    const [latJ, lonJ] = ring[j]!;
    sum += (lonJ + lonI) * Math.cos(((latJ + latI) / 2) * RAD) * (latJ - latI);
  }
  return (Math.abs(sum) * KM_PER_DEGREE * KM_PER_DEGREE) / 2;
}

/** Perimeter of a closed ring in km — the AOI insights companion to `shoelaceKm2`. */
export function ringPerimeterKm(pts: readonly PointLike[]): number {
  if (pts.length < 2) return 0;
  let d = 0;
  for (let i = 0; i < pts.length; i++) d += haversineKm(pts[i]!, pts[(i + 1) % pts.length]!);
  return d;
}

interface GeometryLike {
  type?: string;
  coordinates?: unknown;
}

/**
 * §G7: "polygon centroid = mean of largest ring vertices". The *largest* ring by vertex
 * count, not by area — that is the mockup's `centroidOf`, and it is the cheap proxy that
 * keeps a mainland unit's symbol off its offshore islands.
 *
 * Returns `[lat, lon]` — Leaflet's order, because every caller is placing a marker — or
 * `null` for a geometry with no usable ring (a Point layer, a null geometry, a unit whose
 * simplified column collapsed to nothing).
 */
export function polygonCentroid(geometry: unknown): [number, number] | null {
  const rings = ringsOf(geometry);
  if (!rings.length) return null;

  let best = rings[0]!;
  for (const ring of rings) if (ring.length > best.length) best = ring;
  if (!best.length) return null;

  let x = 0;
  let y = 0;
  for (const [lon, lat] of best) {
    x += lon;
    y += lat;
  }
  return [y / best.length, x / best.length];
}

/**
 * The **outer** ring of each polygon — client round 4 E4, the geometry a mask is cut from.
 *
 * `ringsOf` returns every ring, holes included, because its caller
 * (`polygonCentroid`) only wants the vertex with the most neighbours and does not care
 * which ring is which. A clip path does care: CSS `path()` unions its subpaths under the
 * default `nonzero` fill rule, so handing it the holes as well would *fill* them — a
 * commune with a lake in it would be masked to its own outline plus the lake, which is the
 * same shape, drawn twice as expensively. Dropping them costs nothing (nobody wants a raster
 * to disappear over a lake) and makes the string as short as the shape allows.
 *
 * GeoJSON puts the outer ring first in every polygon (RFC 7946 §3.1.6), so that is the one
 * kept — from a Polygon, one; from a MultiPolygon, one per part, which is how an island
 * commune keeps its islands. Anything that is not a polygon has no outer ring and answers
 * with none.
 */
export function outerRingsOf(geometry: unknown): Position[][] {
  const g = geometry as GeometryLike | null | undefined;
  if (!g || !Array.isArray(g.coordinates)) return [];
  if (g.type === 'Polygon') {
    const outer = (g.coordinates as Position[][])[0];
    return Array.isArray(outer) ? [outer] : [];
  }
  if (g.type === 'MultiPolygon') {
    return (g.coordinates as Position[][][]).flatMap((poly) => {
      const outer = Array.isArray(poly) ? poly[0] : undefined;
      return Array.isArray(outer) ? [outer] : [];
    });
  }
  return [];
}

/** Every linear ring of a Polygon / MultiPolygon, as `[lon, lat]` pairs. */
export function ringsOf(geometry: unknown): Position[][] {
  const g = geometry as GeometryLike | null | undefined;
  if (!g || !Array.isArray(g.coordinates)) return [];
  if (g.type === 'Polygon') return (g.coordinates as Position[][]).filter(Array.isArray);
  if (g.type === 'MultiPolygon')
    return (g.coordinates as Position[][][]).flatMap((poly) =>
      Array.isArray(poly) ? poly.filter(Array.isArray) : [],
    );
  return [];
}

/**
 * §F5.5's placement rule in one call: the API's `properties.centroid` (`[lon, lat]`, from
 * PostGIS `ST_PointOnSurface`) if it is there, else the §G7 mean-of-vertices fallback.
 * Both come back as Leaflet's `[lat, lon]`.
 */
export function unitCentroid(
  centroid: unknown,
  geometry: unknown,
): [number, number] | null {
  if (
    Array.isArray(centroid) &&
    centroid.length >= 2 &&
    Number.isFinite(centroid[0]) &&
    Number.isFinite(centroid[1])
  ) {
    return [Number(centroid[1]), Number(centroid[0])];
  }
  return polygonCentroid(geometry);
}

/**
 * Amendment 1, §useUnitLink: a small ray-casting point-in-ring test — both `pt` and `ring`
 * in `[lon, lat]`, the GeoJSON order. Standard even-odd crossing-number algorithm.
 */
function pointInRing(pt: Position, ring: readonly Position[]): boolean {
  const [x, y] = pt;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    const crosses = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

/**
 * Is `point` inside a Polygon/MultiPolygon `geometry`? Holes are not subtracted — good
 * enough for a display-only map-click resolution (§useUnitLink), and immaterial for the
 * intervention watersheds, none of which carry an inner ring.
 */
export function pointInGeometry(point: PointLike, geometry: unknown): boolean {
  const [lat, lon] = toLatLon(point);
  return outerRingsOf(geometry).some((ring) => pointInRing([lon, lat], ring));
}

/**
 * The first feature of a `FeatureCollection` whose geometry contains `point`, or `null` —
 * the watershed-or-country map-click resolution's own lookup (§useUnitLink), generic over
 * whatever properties shape the collection carries.
 */
export function findContainingFeature<P>(
  point: PointLike,
  fc: { features: readonly { geometry: unknown; properties: P }[] } | null | undefined,
): P | null {
  if (!fc) return null;
  for (const feature of fc.features) {
    if (feature.geometry && pointInGeometry(point, feature.geometry)) return feature.properties;
  }
  return null;
}
